export interface AnnotationBox {
    frameNumber: number;
    vx: number;
    vy: number;
    vw: number;
    vh: number;
    objectId: number;
    playerId?: number;
}

export type Annotations = Map<number, AnnotationBox[]>;

export interface EditResult {
    map: Annotations;
    changed: number;
    conflicts: number[];
}

const framesInRange = (map: Annotations, start: number, end: number, ids: number[]) =>
    Array.from(map.keys()).filter(f => f >= start && f <= end && map.get(f)!.some(b => ids.includes(b.playerId!)));

const replaceFrames = (map: Annotations, frames: number[], update: (boxes: AnnotationBox[]) => AnnotationBox[]): Annotations => {
    const next = new Map(map);
    frames.forEach(f => next.set(f, update(map.get(f)!)));
    return next;
};

export const setPlayerId = (map: Annotations, frame: number, index: number, playerId: number): EditResult => {
    const boxes = map.get(frame) || [];
    if (boxes.some((b, i) => i !== index && b.playerId === playerId)) return { map, changed: 0, conflicts: [frame] };
    return { map: replaceFrames(map, [frame], bs => bs.map((b, i) => (i === index ? { ...b, playerId } : b))), changed: 1, conflicts: [] };
};

export const deleteBox = (map: Annotations, frame: number, index: number): EditResult => ({
    map: replaceFrames(map, [frame], bs => bs.filter((_, i) => i !== index)),
    changed: 1,
    conflicts: [],
});

/** Rename fromId to toId in [start, end]. Refused (conflicts listed) if toId already exists in any of those frames. */
export const reassignRange = (map: Annotations, start: number, end: number, fromId: number, toId: number): EditResult => {
    const frames = framesInRange(map, start, end, [fromId]);
    const conflicts = frames.filter(f => map.get(f)!.some(b => b.playerId === toId));
    if (conflicts.length > 0) return { map, changed: 0, conflicts };
    return { map: replaceFrames(map, frames, bs => bs.map(b => (b.playerId === fromId ? { ...b, playerId: toId } : b))), changed: frames.length, conflicts };
};

const swapId = (b: AnnotationBox, idA: number, idB: number): AnnotationBox =>
    b.playerId === idA ? { ...b, playerId: idB } : b.playerId === idB ? { ...b, playerId: idA } : b;

export const exchangeRange = (map: Annotations, start: number, end: number, idA: number, idB: number): EditResult => {
    const frames = framesInRange(map, start, end, [idA, idB]);
    return { map: replaceFrames(map, frames, bs => bs.map(b => swapId(b, idA, idB))), changed: frames.length, conflicts: [] };
};

export const deleteRange = (map: Annotations, start: number, end: number, id: number): EditResult => {
    const frames = framesInRange(map, start, end, [id]);
    return { map: replaceFrames(map, frames, bs => bs.filter(b => b.playerId !== id)), changed: frames.length, conflicts: [] };
};

/** Fill frames in [start, end] where id is missing, interpolating between its nearest existing boxes before and after. Existing boxes are kept. */
export const interpolateRange = (map: Annotations, start: number, end: number, id: number): EditResult => {
    const keys = Array.from({ length: end - start + 1 }, (_, i) => (map.get(start + i) || []).find(b => b.playerId === id))
        .filter((b): b is AnnotationBox => !!b);
    const next = new Map(map);
    let changed = 0;
    keys.slice(1).forEach((b, i) => {
        const a = keys[i];
        const span = b.frameNumber - a.frameNumber;
        for (let f = a.frameNumber + 1; f < b.frameNumber; f++) {
            const t = (f - a.frameNumber) / span;
            next.set(f, [...(map.get(f) || []), {
                ...a,
                frameNumber: f,
                vx: a.vx + (b.vx - a.vx) * t,
                vy: a.vy + (b.vy - a.vy) * t,
                vw: a.vw + (b.vw - a.vw) * t,
                vh: a.vh + (b.vh - a.vh) * t,
            }]);
            changed++;
        }
    });
    return { map: next, changed, conflicts: [] };
};

const noPid = (pid?: number) => pid === undefined || Number.isNaN(pid);

/** Add startBox alone, or interpolate startBox..endBox over every frame between. Refused (conflicts listed) if a frame already has the same objectId and playerId. */
export const addBoxes = (map: Annotations, startBox: AnnotationBox, endBox: AnnotationBox = startBox): EditResult => {
    const [a, b] = startBox.frameNumber <= endBox.frameNumber ? [startBox, endBox] : [endBox, startBox];
    const span = b.frameNumber - a.frameNumber;
    const boxes = Array.from({ length: span + 1 }, (_, i) => {
        const t = span ? i / span : 0;
        return {
            ...a,
            frameNumber: a.frameNumber + i,
            vx: a.vx + (b.vx - a.vx) * t,
            vy: a.vy + (b.vy - a.vy) * t,
            vw: a.vw + (b.vw - a.vw) * t,
            vh: a.vh + (b.vh - a.vh) * t,
        };
    });
    const conflicts = boxes
        .filter(box => (map.get(box.frameNumber) || []).some(x => x.objectId === box.objectId && (x.playerId === box.playerId || (noPid(x.playerId) && noPid(box.playerId)))))
        .map(box => box.frameNumber);
    if (conflicts.length > 0) return { map, changed: 0, conflicts };
    const next = new Map(map);
    boxes.forEach(box => next.set(box.frameNumber, [...(map.get(box.frameNumber) || []), box]));
    return { map: next, changed: boxes.length, conflicts };
};

export const toLabelCsv = (map: Annotations): string => {
    const rows = Array.from(map.keys())
        .sort((a, b) => a - b)
        .reduce<string[]>((acc, f) => {
            map.get(f)!.forEach(b => {
                const pid = b.playerId === undefined || Number.isNaN(b.playerId) ? '' : String(b.playerId);
                acc.push([b.frameNumber, b.vx, b.vy, b.vw, b.vh, b.objectId, pid].join(','));
            });
            return acc;
        }, []);
    return `frameNumber,vx,vy,vw,vh,objectId,playerId\n${rows.join('\n')}\n`;
};
